import React from 'react';
import { useIntl } from 'react-intl';
import RemoveCircleIcon from '@material-ui/icons/RemoveCircleSharp';
import UndoIcon from '@material-ui/icons/UndoSharp';
import {
  RejectIcon,
  ApproveIcon,
  ProposeAllIcon as BulkForwardIcon
} from '~/modules/common/components/Icons';
import { useRequestStatusBulkHandlers } from '~/modules/resourcing/common/hooks';
import { ResourceRequestStatus } from '~/types';

export const useResourceRequestBulkActions = ({
  resourceRequests,
  openRemoveDialog,
  project,
  disabled
}) => {
  const intl = useIntl();
  const {
    onAllResourceRequestsToBeSubmitted,
    onAllResourceRequestsToBeUnSubmitted,
    onAllResourceRequestsToBeAccepted,
    onAllResourceRequestsToBeAllocationRejected
  } = useRequestStatusBulkHandlers({ project, resourceRequests });

  const getActions = () => {
    const isAnyDraftRequestExists = resourceRequests.some(
      r => r.requestStatus === ResourceRequestStatus.Draft
    );

    const isAnyRejectedRequestExists = resourceRequests.some(
      r => r.requestStatus === ResourceRequestStatus.Rejected
    );

    const isAnySubmittedRequestExists = resourceRequests.some(
      r => r.requestStatus === ResourceRequestStatus.Submitted
    );

    const isAnyTentativeRequestExists = resourceRequests.some(
      r => r.requestStatus === ResourceRequestStatus.Tentative
    );

    const isAnyAllocationRejectedRequestExists = resourceRequests.some(
      r => r.requestStatus === ResourceRequestStatus.Allocationrejected
    );

    const isAnyRemovableResourceRequestExists = resourceRequests.some(
      r =>
        r.requestStatus !== ResourceRequestStatus.Tentative &&
        r.requestStatus !== ResourceRequestStatus.Complete
    );

    const submitAllAction = {
      icon: <BulkForwardIcon />,
      className: 'submitIcon',
      onClick: onAllResourceRequestsToBeSubmitted,
      primary: true,
      disabled,
      label: intl.formatMessage({
        id: 'resourceRequestActions.submitAllRequests'
      })
    };
    const unsubmitAllAction = {
      icon: <UndoIcon />,
      onClick: onAllResourceRequestsToBeUnSubmitted,
      disabled,
      label: intl.formatMessage({
        id: 'resourceRequestActions.unsubmitAllRequests'
      })
    };

    const removeAllAction = {
      icon: <RemoveCircleIcon />,
      className: 'removeIcon',
      onClick: openRemoveDialog,
      disabled,
      label: intl.formatMessage({
        id: 'resourceRequestActions.removeAllRequests'
      })
    };

    const acceptAllAction = {
      icon: <ApproveIcon />,
      className: 'approveIcon',
      onClick: onAllResourceRequestsToBeAccepted,
      primary: !(isAnyDraftRequestExists || isAnyRejectedRequestExists),
      disabled,
      label: intl.formatMessage({
        id: 'resourceRequestActions.acceptAllResources'
      })
    };

    const rejectAllAction = {
      icon: <RejectIcon />,
      className: 'rejectIcon',
      onClick: onAllResourceRequestsToBeAllocationRejected,
      primary: false,
      disabled,
      label: intl.formatMessage({
        id: 'resourceRequestActions.rejectAllResources'
      })
    };

    return [
      ...(isAnyDraftRequestExists || isAnyRejectedRequestExists
        ? [submitAllAction]
        : []),
      ...(isAnySubmittedRequestExists || isAnyAllocationRejectedRequestExists
        ? [unsubmitAllAction]
        : []),
      ...(isAnyTentativeRequestExists
        ? [acceptAllAction, rejectAllAction]
        : []),
      ...(isAnyRemovableResourceRequestExists ? [removeAllAction] : [])
    ];
  };

  return {
    actions: resourceRequests.length !== 0 ? getActions() : []
  };
};

export default useResourceRequestBulkActions;
